import React, { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * One playable video a render produced. A pipeline run yields several, a caption or story
 * render yields one — the panel reads both the same way, so nothing branches on kind.
 */
/** One re-render of a clip the author edited. Newest first. */
export interface RenderEdit {
  id: string;
  status: string;
  stage: string;
  progress: number;
  url: string | null;
  error: string | null;
  createdAt: string;
}

export interface RenderOutput {
  /** The clip this output is, when the render made clips. Null for a story or caption render. */
  shortId: string | null;
  index: number;
  title: string | null;
  /** Rejection reason on a candidate row (see isCandidate); the clip's own caption otherwise. */
  caption?: string | null;
  url: string | null;
  durationSeconds: number | null;
  score: number | null;
  /** Where the clip sits in the source video. Null for a render that is not a clip. */
  startTime: number | null;
  endTime: number | null;
  /** Ordered source ranges for a summary/recap short; null for a normal single-range clip. */
  segments: Array<{ start: number; end: number }> | null;
  /** Studio mode only — engine kept this clip under a weak signal. */
  lowConfidence?: boolean | null;
  /** Studio mode only — the per-factor score behind `score`. */
  scoreBreakdown?: Record<string, number> | null;
  /** Studio mode only — which reframe strategy produced this clip's crop, when known. */
  reframe?: 'active-speaker' | 'fit-all-faces' | 'none' | 'mixed' | null;
  /** Studio mode only, STUDIO_TECHNICAL_ANALYSIS=1 — a rejected/paired-only candidate rendered
   *  for review rather than a real accepted/low-confidence short. Playable and editable exactly
   *  like every other row — `caption` carries the rejection reason for these. */
  isCandidate?: boolean;
  /** Size of the pipeline's cut. Null for an export, whose own size is not recorded. */
  fileSize: number | null;
  /** Every export made from this clip, newest first; the clip's own cut is the original. */
  edits: RenderEdit[];
}

/** The Shorts item a render has already been turned into, if any. */
export interface DerivedShorts {
  videoId: string;
  name: string;
  projectId: string | null;
}

export interface RenderJob {
  id: string;
  status: string;
  stage: string;
  progress: number;
  kineticCaption: Record<string, any> | null;
  /** First output's URL, kept so callers that only ever expect one keep working. */
  outputUrl: string | null;
  outputs: RenderOutput[];
  /**
   * What this render was created with, already vetted server-side. The stored job settings hold
   * a live API key, so the API sends this list rather than the settings object itself.
   */
  details: Array<{ label: string; value: string }>;
  /** The raw upload standing in for a render — the video was posted as-is, never processed. */
  isSource?: boolean;
  /** Present once "Generate Shorts" has been run on this render. */
  derivedShorts?: DerivedShorts | null;
  error: string | null;
  createdAt: string;
}

interface RendersPanelProps {
  /** Caption workspace whose renders to list. Ignored when `jobsUrl` is given. */
  workspaceId?: string;
  /**
   * Where to fetch renders from, for a surface that is not a caption workspace — a Story lists
   * its own the same way. Both sources return the same row shape.
   */
  jobsUrl?: string;
  /** Shown in place of the preset name, for renders that have no caption preset. */
  labelFor?: (job: RenderJob) => string;
  /** Copy for the empty state, which differs by what the surface asks the author to do. */
  emptyText?: string;
  onJobsChange: (jobs: RenderJob[]) => void;
  /** Absent on a surface that cannot re-run a render, which hides the menu item entirely. */
  onRegenerate?: (config: Record<string, any>) => void;
  /**
   * `output` is the specific video clicked. It is omitted when the row itself was clicked and
   * the render made only one, which is every caption and story render.
   */
  onView: (job: RenderJob, output?: RenderOutput, edit?: RenderEdit | null) => void;
  /** Opens the editor for one output. Absent on surfaces that only play. */
  onEdit?: (job: RenderJob, output: RenderOutput) => void;
  /**
   * Turns a finished video into a Shorts item of its own. `edit` is the specific export clicked;
   * absent means the render's own output. A version that was edited no longer runs on the
   * transcript's timeline, which is what the caller needs `edit` to know.
   */
  onGenerateShorts?: (job: RenderJob, output?: RenderOutput, edit?: RenderEdit | null) => void;
  /** False hides every download link (read-only shares). Defaults to true. */
  canDownload?: boolean;
  /** Opens a Shorts item a render already produced. */
  onOpenDerivedShorts?: (derived: DerivedShorts) => void;
  selectedJobId?: string | null;
  /**
   * Which output of `selectedJobId` is playing, by its own index. Not by URL — every poll
   * presigns a fresh one, so a URL comparison stops matching seconds after the click.
   */
  selectedOutputIndex?: number | null;
  /** Which edit of the selected output is playing; null means the original cut. */
  selectedEditId?: string | null;
  /** Whether the selected output is playing right now, so its row can offer pause. */
  isPlaying?: boolean;
  /** Plays this version, or pauses it when it is the one already playing. */
  onTogglePlay?: (job: RenderJob, output: RenderOutput, edit?: RenderEdit | null) => void;
  refreshSignal?: number;
  /** The item's original upload, shown as a pinned "Source" row above the renders. */
  sourceJob?: RenderJob | null;
}

const badgeClass = (status: string) => {
  if (status === 'done' || status === 'completed') return 'video-status-done';
  if (status === 'failed') return 'video-status-failed';
  if (status === 'pending' || status === 'queued') return 'video-status-pending';
  return 'video-status-processing';
};

const badgeLabel = (job: RenderJob) => {
  if (job.status === 'done' || job.status === 'completed') return 'Ready';
  if (job.status === 'failed') return 'Failed';
  if (job.status === 'queued' || job.status === 'pending') return 'Queued';
  return `${(job.stage || job.status)}…`;
};

const presetLabel = (preset: string | undefined) => {
  if (!preset) return 'Unknown preset';
  return preset.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatClock = (secs: number | null): string => {
  if (secs === null || secs === undefined || secs < 0) return '';
  return `${Math.floor(secs / 60)}:${Math.floor(secs % 60).toString().padStart(2, '0')}`;
};

/** Scores arrive on a 0-10 or 0-100 scale; the clip header shows them out of 100. */
const scoreOutOf100 = (score: number | null): number | null => {
  const n = Number(score);
  if (score === null || score === undefined || !Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round(n <= 10 ? n * 10 : n)));
};

const scoreTone = (score: number): 'high' | 'mid' | 'low' => (score >= 75 ? 'high' : score >= 50 ? 'mid' : 'low');

const RendersPanel: React.FC<RendersPanelProps> = ({
  workspaceId, jobsUrl, labelFor, emptyText, onJobsChange, onRegenerate, onView, onEdit,
  onGenerateShorts, onOpenDerivedShorts, canDownload = true,
  selectedJobId, selectedOutputIndex, selectedEditId, isPlaying, onTogglePlay, refreshSignal, sourceJob,
}) => {
  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Which clips have their version list open. Keyed by job and clip, since clip indexes repeat
  // across renders.
  const [openClips, setOpenClips] = useState<Set<string>>(new Set());
  const seededRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      const url = jobsUrl || `${API_BASE}/api/caption-workspaces/${workspaceId}/jobs`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data: RenderJob[] = await res.json();
        setJobs(data);
        onJobsChange(data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [workspaceId, jobsUrl, onJobsChange]);

  useEffect(() => { fetchJobs(); }, [fetchJobs, refreshSignal]);

  // Pointing the panel at another item makes everything it holds belong to the old one. Without
  // this the previous item's rows stay on screen until the first fetch lands, and its expansion
  // never re-seeds — the new item's newest render would open collapsed.
  useEffect(() => {
    seededRef.current = false;
    setJobs([]);
    setExpanded(new Set());
    setLoading(true);
  }, [workspaceId, jobsUrl]);

  useEffect(() => {
    // Poll quickly while something is rendering, slowly otherwise — but always. Polling only
    // when the list already held a live job meant a render started after this mounted never
    // appeared, leaving the newest row an older, finished one.
    const hasNonTerminal = jobs.some(j => !['done', 'completed', 'failed'].includes(j.status));
    pollRef.current = setInterval(fetchJobs, hasNonTerminal ? 2000 : 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobs, fetchJobs]);

  // The newest render is the one the author just made, so it opens; older ones stay collapsed
  // to their header, which is what keeps a long history readable in a side panel.
  useEffect(() => {
    if (!seededRef.current && jobs.length > 0) {
      seededRef.current = true;
      setExpanded(new Set([jobs[0].id]));
    }
  }, [jobs]);

  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.kc-render-menu')) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  if (loading) {
    return <div className="kc-renders-empty">Loading renders…</div>;
  }

  // The original upload is pinned above the renders, unless the API already listed it as one.
  const listed = sourceJob && !jobs.some(j => j.isSource) ? [sourceJob, ...jobs] : jobs;

  if (listed.length === 0) {
    return <div className="kc-renders-empty">{emptyText || 'No renders yet. Pick a style and click Generate.'}</div>;
  }

  return (
    <div className="kc-renders-list">
      {listed.map(job => {
        // A caller that has not been updated yet still sends a single `outputUrl`; treat it as
        // the one-output render it is rather than rendering an empty row.
        const outputs = (job.outputs && job.outputs.length)
          ? job.outputs
          : (job.outputUrl
              ? [{ shortId: null, index: 0, title: null, url: job.outputUrl,
                   durationSeconds: null, score: null,
                   startTime: null, endTime: null, segments: null, fileSize: null, edits: [] }]
              : []);

        // Only a render that made several videos needs to nest. Keeping a one-output render a
        // plain clickable row is what leaves caption and story surfaces behaving as they did.
        const isBatch = outputs.length > 1;
        // A render with one output still nests once that clip has versions — a story render is
        // a clip too, and its exports belong under it rather than nowhere.
        const hasNested = isBatch || (outputs[0]?.edits?.length ?? 0) > 0;
        const isOpen = expanded.has(job.id);
        const failed = job.status === 'failed';
        const hasVideo = outputs.length > 0;
        const isActive = job.id === selectedJobId;

        const toggle = () => setExpanded(prev => {
          const next = new Set(prev);
          if (next.has(job.id)) next.delete(job.id); else next.add(job.id);
          return next;
        });

        return (
          <div key={job.id} className="kc-render-group">
            <div
              className={`kc-render-row ${hasVideo ? 'kc-render-row--clickable' : ''} ${isActive && !hasNested ? 'kc-render-row--active' : ''}`}
              onClick={() => {
                if (hasNested) toggle();
                else if (hasVideo) onView(job, outputs[0]);
              }}
            >
              {!hasNested && hasVideo && onTogglePlay ? (
                <button
                  className="kc-render-icon kc-render-play"
                  title={isActive && isPlaying ? 'Pause' : 'Play'}
                  aria-label={isActive && isPlaying ? 'Pause' : 'Play'}
                  onClick={(e) => { e.stopPropagation(); onTogglePlay(job, outputs[0]); }}
                >
                  {isActive && isPlaying ? '⏸' : '▶'}
                </button>
              ) : (
                <div className="kc-render-icon">
                  {hasNested ? (isOpen ? '▾' : '▸') : (failed ? '⚠' : hasVideo ? '🎬' : '⏳')}
                </div>
              )}

              <div className="kc-render-row-main">
                <span className="kc-render-preset">
                  {labelFor ? labelFor(job) : presetLabel(job.kineticCaption?.preset)}
                </span>
                <span className="kc-render-time">
                  {formatTime(job.createdAt)}
                  {isBatch && ` · ${outputs.length} videos`}
                </span>
              </div>

              {job.isSource
                ? <span className="video-status-badge video-status-info">Source</span>
                : <span className={`video-status-badge ${badgeClass(job.status)}`}>{badgeLabel(job)}</span>}

              <div className="kc-render-menu" onClick={(e) => e.stopPropagation()}>
                <button
                  className="project-card-menu kc-render-menu-trigger"
                  onClick={() => setOpenMenuId(openMenuId === job.id ? null : job.id)}
                >
                  ⋯
                </button>
                {openMenuId === job.id && (
                  <div className="project-card-dropdown">
                    {hasVideo && !hasNested && (
                      <button onClick={() => { onView(job, outputs[0]); setOpenMenuId(null); }}>▶ View</button>
                    )}
                    {hasVideo && !hasNested && (
                      canDownload && <a href={outputs[0].url!} target="_blank" rel="noreferrer" onClick={() => setOpenMenuId(null)}>⬇ Download</a>
                    )}
                    {onEdit && !hasNested && outputs[0]?.shortId && (
                      <button onClick={() => { onEdit(job, outputs[0]); setOpenMenuId(null); }}>✎ Edit clip</button>
                    )}
                    {/* A nested render keeps its clips behind an expander, so the menu has to
                        say so — otherwise a collapsed render offers no route into them. */}
                    {hasNested && (
                      <button onClick={() => { toggle(); setOpenMenuId(null); }}>
                        {isOpen
                          ? '▴ Hide'
                          : isBatch ? `▾ Show ${outputs.length} videos to edit` : '▾ Show versions'}
                      </button>
                    )}
                    {onGenerateShorts && hasVideo && !hasNested && !job.isSource && (
                      <button onClick={() => { onGenerateShorts(job, outputs[0], null); setOpenMenuId(null); }}>✂ Generate Shorts</button>
                    )}
                    {onRegenerate && job.kineticCaption && (
                      <button onClick={() => { onRegenerate!(job.kineticCaption!); setOpenMenuId(null); }}>↻ Regenerate</button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* A render that has already produced a Shorts item says so on the row, so the
                author has a route into it without going back to the project list. */}
            {job.derivedShorts && (
              <button
                className="kc-render-derived"
                onClick={(e) => { e.stopPropagation(); onOpenDerivedShorts?.(job.derivedShorts!); }}
                disabled={!onOpenDerivedShorts}
                title={`Open ${job.derivedShorts.name}`}
              >
                <span className="kc-render-derived-icon">✂</span>
                <span className="kc-render-derived-name">{job.derivedShorts.name}</span>
                <span className="kc-render-derived-go">›</span>
              </button>
            )}

            {hasNested && isOpen && (
              <div className="kc-render-outputs">
                {outputs.map(o => {
                  const clipKey = `${job.id}:${o.index}`;
                  const edits = o.edits || [];
                  const hasEdits = edits.length > 0;
                  const clipOpen = openClips.has(clipKey);
                  const isCurrentClip = isActive && selectedOutputIndex === o.index;
                  // The original counts as a version, so two rows only appear once an edit exists.
                  const playingOriginal = isCurrentClip && !selectedEditId;

                  const toggleClip = () => setOpenClips(prev => {
                    const next = new Set(prev);
                    if (next.has(clipKey)) next.delete(clipKey); else next.add(clipKey);
                    return next;
                  });

                  const score = scoreOutOf100(o.score);
                  // Active speaker has its own icon flag; the other reframe strategies keep a label.
                  const reframeLabel = o.reframe === 'fit-all-faces' ? '▭ fit all faces'
                    : o.reframe === 'mixed' ? '▭ mixed reframe'
                    : o.reframe === 'none' ? '▭ no crop plan'
                    : null;

                  return (
                    <div className="kc-render-clip" key={clipKey}>
                      <div
                        className={`kc-render-output ${playingOriginal && !hasEdits ? 'kc-render-output--active' : ''}`}
                        onClick={() => (hasEdits ? toggleClip() : onView(job, o, null))}
                      >
                        {/* The thumb slot holds the transport control and index today; a clip
                            thumbnail can be dropped in here later without touching the row. */}
                        <span className="kc-render-output-thumb">
                          {onTogglePlay && !hasEdits && (
                            <button
                              className="kc-render-output-play"
                              title={playingOriginal && isPlaying ? 'Pause' : 'Play'}
                              aria-label={playingOriginal && isPlaying ? 'Pause' : 'Play'}
                              onClick={(e) => { e.stopPropagation(); onTogglePlay(job, o, null); }}
                            >
                              {playingOriginal && isPlaying ? '⏸' : '▶'}
                            </button>
                          )}
                          {hasEdits && (
                            <span className="kc-render-output-play kc-render-output-chevron">
                              {clipOpen ? '▾' : '▸'}
                            </span>
                          )}
                          <span className="kc-render-output-idx">{o.index}</span>
                        </span>
                        <div className="kc-render-output-body">
                          <span className="kc-render-output-title">{o.title || `Clip ${o.index}`}</span>
                          <div className="kc-render-output-meta">
                            {score !== null && (
                              <span className={`kc-render-output-stat kc-render-output-score--${scoreTone(score)}`} title="Confidence score">{score}</span>
                            )}
                            {formatClock(o.durationSeconds) && (
                              <span className="kc-render-output-stat">{formatClock(o.durationSeconds)}</span>
                            )}
                            {o.isCandidate ? (
                              <span
                                className="kc-render-flag kc-render-flag--candidate"
                                title={o.caption ? `Group: Candidates — ${o.caption}` : 'Group: Candidates — not an accepted short'}
                              >
                                C
                              </span>
                            ) : o.shortId && (
                              <span className="kc-render-flag kc-render-flag--short" title="Group: Shorts">S</span>
                            )}
                            {o.lowConfidence && (
                              <span
                                className="kc-render-flag kc-render-flag--lowconf"
                                title="Low confidence — Studio kept this clip under a weak signal, review before publishing"
                                aria-label="Low confidence"
                              >
                                ⚠
                              </span>
                            )}
                            {o.reframe === 'active-speaker' && (
                              <span
                                className="kc-render-flag kc-render-flag--speaker"
                                title="Active speaker — the crop follows whoever is talking"
                                aria-label="Active speaker"
                              >
                                🎙
                              </span>
                            )}
                            {reframeLabel && (
                              <span className="kc-render-edits-count" title="Reframe strategy used for this clip's crop">
                                {reframeLabel}
                              </span>
                            )}
                            {hasEdits && (
                              <span className="kc-render-edits-count">✎ {edits.length}</span>
                            )}
                          </div>
                        </div>
                        {/* Same menu affordance the render row above uses, so every level of
                            the list is opened the same way. */}
                        <div className="kc-render-menu" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="project-card-menu kc-render-menu-trigger"
                            title="Clip actions"
                            aria-label="Clip actions"
                            onClick={() => setOpenMenuId(openMenuId === clipKey ? null : clipKey)}
                          >
                            ⋯
                          </button>
                          {openMenuId === clipKey && (
                            <div className="project-card-dropdown">
                              {o.url && (
                                <button onClick={() => { onView(job, o, null); setOpenMenuId(null); }}>▶ Play original</button>
                              )}
                              {onEdit && o.shortId && (
                                <button onClick={() => { onEdit(job, o); setOpenMenuId(null); }}>✎ Edit clip</button>
                              )}
                              {hasEdits && (
                                <button onClick={() => { toggleClip(); setOpenMenuId(null); }}>
                                  {clipOpen ? '▴ Hide versions' : `▾ Show ${edits.length} version${edits.length > 1 ? 's' : ''}`}
                                </button>
                              )}
                              {onGenerateShorts && o.url && (
                                <button onClick={() => { onGenerateShorts(job, o, null); setOpenMenuId(null); }}>✂ Generate Shorts</button>
                              )}
                              {o.url && (
                                canDownload && <a href={o.url} target="_blank" rel="noreferrer" onClick={() => setOpenMenuId(null)}>⬇ Download original</a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Newest edit first, the clip's own cut last: the version the author most
                          recently made is the one they are looking for. */}
                      {hasEdits && clipOpen && (
                        <div className="kc-render-versions">
                          {edits.map((ed, i) => {
                            const version = edits.length - i + 1;
                            const current = isCurrentClip && selectedEditId === ed.id;
                            const ready = !!ed.url;
                            return (
                              <div
                                key={ed.id}
                                className={`kc-render-version ${current ? 'kc-render-version--active' : ''}`}
                                onClick={() => { if (ready) onView(job, o, ed); }}
                              >
                                {onTogglePlay && ready ? (
                                  <button
                                    className="kc-render-output-play"
                                    title={current && isPlaying ? 'Pause' : 'Play'}
                                    aria-label={current && isPlaying ? 'Pause' : 'Play'}
                                    onClick={(e) => { e.stopPropagation(); onTogglePlay(job, o, ed); }}
                                  >
                                    {current && isPlaying ? '⏸' : '▶'}
                                  </button>
                                ) : (
                                  <span className="kc-render-output-play">{ed.status === 'failed' ? '⚠' : '⏳'}</span>
                                )}
                                <span className="kc-render-version-name">Edit v{version}</span>
                                <span className="kc-render-version-time">{formatTime(ed.createdAt)}</span>
                                {!ready && (
                                  <span className={`video-status-badge ${badgeClass(ed.status)}`}>
                                    {badgeLabel({ ...ed, kineticCaption: null } as unknown as RenderJob)}
                                  </span>
                                )}
                                <div className="kc-render-menu" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    className="project-card-menu kc-render-menu-trigger"
                                    title="Version actions"
                                    aria-label="Version actions"
                                    onClick={() => setOpenMenuId(openMenuId === `${clipKey}:${ed.id}` ? null : `${clipKey}:${ed.id}`)}
                                  >
                                    ⋯
                                  </button>
                                  {openMenuId === `${clipKey}:${ed.id}` && (
                                    <div className="project-card-dropdown">
                                      {ed.url && (
                                        <button onClick={() => { onView(job, o, ed); setOpenMenuId(null); }}>▶ Play</button>
                                      )}
                                      {onEdit && o.shortId && (
                                        <button onClick={() => { onEdit(job, o); setOpenMenuId(null); }}>✎ Edit clip again</button>
                                      )}
                                      {onGenerateShorts && ed.url && (
                                        <button onClick={() => { onGenerateShorts(job, o, ed); setOpenMenuId(null); }}>✂ Generate Shorts</button>
                                      )}
                                      {ed.url && (
                                        canDownload && <a href={ed.url} target="_blank" rel="noreferrer" onClick={() => setOpenMenuId(null)}>⬇ Download</a>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          <div
                            className={`kc-render-version ${playingOriginal ? 'kc-render-version--active' : ''}`}
                            onClick={() => onView(job, o, null)}
                          >
                            {onTogglePlay && (
                              <button
                                className="kc-render-output-play"
                                title={playingOriginal && isPlaying ? 'Pause' : 'Play'}
                                aria-label={playingOriginal && isPlaying ? 'Pause' : 'Play'}
                                onClick={(e) => { e.stopPropagation(); onTogglePlay(job, o, null); }}
                              >
                                {playingOriginal && isPlaying ? '⏸' : '▶'}
                              </button>
                            )}
                            <span className="kc-render-version-name">Original</span>
                            <span className="kc-render-version-time">{formatTime(job.createdAt)}</span>
                            <div className="kc-render-menu" onClick={(e) => e.stopPropagation()}>
                              <button
                                className="project-card-menu kc-render-menu-trigger"
                                title="Original actions"
                                aria-label="Original actions"
                                onClick={() => setOpenMenuId(openMenuId === `${clipKey}:original` ? null : `${clipKey}:original`)}
                              >
                                ⋯
                              </button>
                              {openMenuId === `${clipKey}:original` && (
                                <div className="project-card-dropdown">
                                  {o.url && (
                                    <button onClick={() => { onView(job, o, null); setOpenMenuId(null); }}>▶ Play</button>
                                  )}
                                  {onEdit && o.shortId && (
                                    <button onClick={() => { onEdit(job, o); setOpenMenuId(null); }}>✎ Edit clip</button>
                                  )}
                                  {o.url && (
                                    canDownload && <a href={o.url} target="_blank" rel="noreferrer" onClick={() => setOpenMenuId(null)}>⬇ Download</a>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RendersPanel;
